import { useState, useEffect, useCallback } from "react";
import moment from "moment";

/**
 * Custom hook to manage and validate event date/time and all-day logic.
 * @param {Object} options
 * @param {function} [options.t] - Optional translation function for error messages.
 * @param {Date|moment} [options.initialEventDate]
 * @param {moment|null} [options.initialStartTime]
 * @param {moment|null} [options.initialEndTime]
 * @param {boolean} [options.initialIsAllDayEvent]
 * @param {function|Object} [options.setErrors]
 * @returns {Object}
 */

export default function useEventDateTimeSync({
  t,
  initialEventDate = moment().toDate(),
  initialStartTime = null,
  initialEndTime = null,
  initialIsAllDayEvent = false,
  setErrors,
} = {}) {
  const [eventDate, setEventDate] = useState(initialEventDate);
  const [startTime, setStartTime] = useState(initialStartTime);
  const [endTime, setEndTime] = useState(initialEndTime);
  const [isAllDayEvent, setIsAllDayEvent] = useState(initialIsAllDayEvent);
  const [endTimeError, setEndTimeError] = useState("");

  // Helper for error message
  const getEndTimeErrorMsg = useCallback(() => {
    return t
      ? t("form.errors.endTimeShouldBeGreater")
      : "End time must be after start time";
  }, [t]);

  // All Day logic: set times when toggled on
  useEffect(() => {
    if (isAllDayEvent) {
      setStartTime(moment(eventDate).startOf("day"));
      setEndTime(moment(eventDate).hour(23).minute(59).second(0));
    }
    // Do not clear times when toggled off; let user control
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllDayEvent, eventDate]);

  // Validation logic
  useEffect(() => {
    if (startTime && endTime && moment(endTime).isSameOrBefore(startTime)) {
      setEndTimeError(getEndTimeErrorMsg());
    } else {
      setEndTimeError("");
    }
  }, [startTime, endTime, getEndTimeErrorMsg]);
  const removeError = useCallback((field) => {
    setErrors((prev) => {
      if (prev[field]) {
        delete prev[field];
        return {
          ...prev,
        };
      }
      return prev;
    });
  }, []);
  // Unified handler for all changes
  const handleDateTimeChange = useCallback(
    (field, value) => {
      switch (field) {
        case "startTime":
          if (isAllDayEvent) setIsAllDayEvent(false);
          setStartTime(value);
          if (value) removeError(field);
          break;
        case "endTime":
          if (isAllDayEvent) {
            const allDayEnd = moment(eventDate).hour(23).minute(59).second(0);
            if (!value || !moment(value).isSame(allDayEnd, "minute")) {
              setIsAllDayEvent(false);
            }
          }
          setEndTime(value);
          if (moment(value).diff(startTime, "minutes") > 0 && value)
            removeError(field);
          break;
        case "eventDate":
          setEventDate(value);
          break;
        case "allDay":
          setIsAllDayEvent(value);
          if (value) {
            setStartTime(null);
            setEndTime(null);
          }
          break;
        default:
          break;
      }
    },
    [isAllDayEvent, eventDate, startTime]
  );
  const startOrEndTimeValue = useCallback(
    (value, startOrEndTime) => {
      if (isAllDayEvent) {
        return moment(eventDate)
          [startOrEndTime]("day")
          .format("YYYY-MM-DDTHH:mm");
      }
      if (!value) {
        return null;
      }
      return (
        moment(eventDate).format("YYYY-MM-DDT") + moment(value).format("HH:mm")
      );
    },
    [isAllDayEvent, eventDate]
  );
  return {
    eventDate,
    setEventDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    isAllDayEvent,
    setIsAllDayEvent,
    endTimeError,
    setEndTimeError,
    handleDateTimeChange,
    startOrEndTimeValue,
  };
}
